import {
  ClearFiltersButton,
  SearchInput,
} from '@/components/data-table/filters';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import type { DeviceModel } from '@octave/contracts';
import { formatPrice } from '@/lib/format';

export interface CatalogFilters {
  brand?: string;
  capacity?: string;
  maxPrice: number;
  searchQuery: string;
}

interface CatalogFiltersProps {
  deviceModels: DeviceModel[];
  filters: CatalogFilters;
  onFiltersChange: (filters: CatalogFilters) => void;
}

export function CatalogFilters({
  deviceModels,
  filters,
  onFiltersChange,
}: CatalogFiltersProps) {
  const uniqueBrands = Array.from(
    new Set(deviceModels.map((model) => model.brand))
  ).sort();

  const uniqueCapacities = Array.from(
    new Set(
      deviceModels
        .flatMap((model) => model.variants)
        .map((variant) => variant.capacity)
        .filter((capacity): capacity is number => capacity !== undefined)
    )
  ).sort((a, b) => a - b);

  const allPrices = deviceModels
    .flatMap((model) => model.variants)
    .map((variant) => variant.price);

  const maxPriceFromData = allPrices.length > 0 ? Math.max(...allPrices) : 0;
  const minPriceFromData = allPrices.length > 0 ? Math.min(...allPrices) : 0;

  const handleChange = <K extends keyof CatalogFilters>(
    field: K,
    value: CatalogFilters[K]
  ) => {
    onFiltersChange({ ...filters, [field]: value });
  };

  const hasActiveFilters =
    (filters.brand && filters.brand !== '') ||
    (filters.capacity && filters.capacity !== '') ||
    (filters.maxPrice && filters.maxPrice < maxPriceFromData) ||
    (filters.searchQuery && filters.searchQuery !== '');

  const handleClear = () => {
    onFiltersChange({
      brand: '',
      capacity: '',
      maxPrice: maxPriceFromData,
      searchQuery: '',
    });
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <SearchInput
        placeholder="Rechercher un appareil..."
        value={filters.searchQuery}
        onChange={(value) => handleChange('searchQuery', value)}
      />

      {/* Brand */}
      <Select
        value={filters.brand || ''}
        onValueChange={(value) => handleChange('brand', value)}
      >
        <SelectTrigger className="w-[160px] bg-white border-slate-200 text-sm">
          <SelectValue placeholder="Marque" />
        </SelectTrigger>
        <SelectContent>
          {uniqueBrands.map((brand) => (
            <SelectItem key={brand} value={brand}>
              {brand}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Capacity */}
      {uniqueCapacities.length > 0 && (
        <Select
          value={filters.capacity || ''}
          onValueChange={(value) => handleChange('capacity', value)}
        >
          <SelectTrigger className="w-[140px] bg-white border-slate-200 text-sm">
            <SelectValue placeholder="Capacité" />
          </SelectTrigger>
          <SelectContent>
            {uniqueCapacities.map((capacity) => (
              <SelectItem key={capacity} value={capacity.toString()}>
                {capacity >= 1024 ? `${capacity / 1024} To` : `${capacity} Go`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Price slider */}
      {maxPriceFromData > 0 && (
        <div className="flex items-center gap-2 w-48">
          <div className="flex-1 space-y-1">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>Prix max</span>
              <span className="font-medium text-slate-600">
                {formatPrice(filters.maxPrice || maxPriceFromData)}
              </span>
            </div>
            <Slider
              value={[filters.maxPrice || maxPriceFromData]}
              onValueChange={(value) => handleChange('maxPrice', value[0])}
              max={maxPriceFromData}
              min={minPriceFromData}
              step={100}
            />
          </div>
        </div>
      )}

      {hasActiveFilters && <ClearFiltersButton onClick={handleClear} />}
    </div>
  );
}
